import { useEffect, useMemo, useState, useRef } from "react"
import { fetchUserAttributes } from "aws-amplify/auth"
import { t } from "src/translations/help"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Outlet } from "react-router-dom"
import { Header } from "src/blitz"
import { useSelector, useDispatch } from "react-redux"
import {
  selectUserAccess,
  setBreadcrumbs,
  setHeaderTitle,
  setUserAccess
} from "src/redux/slicers/homeSlice"
import { selectBuildingSummary } from "src/redux/slicers/buildingSummarySlice"
import { selectEquipmentSummary } from "src/redux/slicers/equipmentSummarySlice"
import { fetchSession } from "src/redux/slicers/sessionSlice"
import { Auth } from "src/shared/lib/amplify"
import { useAuthenticator } from "@aws-amplify/ui-react"
import "@aws-amplify/ui-react/styles.css"
import "react-loading-skeleton/dist/skeleton.css"
import "src/App.scss"
import { Hub } from "src/shared/lib/amplify"
import { ErrorMessage } from "src/components/shared"
import { Typography, Button } from "src/blitz"
import "./styles.scss"
import { useQuery, useMutation } from "src/hooks/APIHooks"
import { GetUIAccessToken } from "src/graphql/queries"
import { accessControlFunc } from "src/components/accessControl"
import { zendeskHelper } from "./zendesk-helper"
import { trackEvent, identifyEvent } from "src/amplitude.js"
import { USER_EVENTS } from "src/amplitude-categories"
import store from "src/redux/Store"
import {
  selectUserInfo,
  setUserInfo,
  setUserRoles
} from "src/redux/slicers/userData"
import { GET_USER_BY_ID } from "src/graphql/queries/userQueries"
import { parseUserData } from "src/parseUserData"
import {
  UPDATE_OWN_SELF,
  UPDATE_USER_SESSION
} from "src/graphql/queries/userMutations"
import { isEmpty } from "lodash"
import {
  FAILURE,
  LOADING,
  SUCCESS
} from "src/components/shared/Charts/constant"
import { setFilter } from "src/redux/slicers/reportSlice"
import { GET_EULA_VERSION } from "src/graphql/queries/s3Query"
import { staticFilesPath } from "../aws-exports.js"
import { getConfig } from "src/config"
import { COMMAND_CENTER_GEN_4_REDIRECTION } from "src/constants/common"
import SUPPORTED_LANGUAGES from "src/blitz/Header/LanguagePicker"
import { setSelectedLanguage } from "src/redux/slicers/userData"
import { clearTranslations } from "src/redux/slicers/translationSlice"
import { setMomentLocale } from "src/utils/MomentUtils"
import translate, { TranslateComponent } from "src/common/translations"
import ItemNotFound, {
  itemNotFoundTitle
} from "src/components/shared/ErrorModal/Errors/ItemNotFound"
import { setApplicationErrors } from "src/redux/slicers/applicationSlice"
import { validate as isValidUUID } from "uuid"
import { decodeURLSearchParams } from "src/components/shared/Charts/Helper/common"
import { isFedRampEnvironment } from "src/utils/CommonMethods"
import GovernmentBanner from "src/components/shared/GovernmentBanner/GovernmentBanner"
import { isValidProviderName } from "src/shared/lib/auth/idpDiscovery"
import i18n from "src/i18nInit"

// Checking whether this is report page or normal app pages
const isReport =
  String(window?.location?.pathname).startsWith("/report/") ||
  String(window?.location?.pathname).startsWith("/DIreport/")

// sessionStorage keys (scoped per browser tab) used to force a fresh login â€“
// i.e. always ask the user for their credentials again â€“ on a new tab.
const FORCED_FRESH_LOGIN_KEY = "cc_forced_fresh_login"
const LOGIN_RETURN_TO_KEY = "cc_login_return_to"// Persists the IdP provider discovered before the Cognito /logout redirect so
// it survives the page reload and is used in the post-redirect sign-in call.
const IDP_PROVIDER_KEY = "cc_idp_provider"
// One-time per-tab guard to avoid repeated signOut redirects in the same tab.
const NEW_TAB_SIGNOUT_DONE_KEY = "cc_new_tab_signout_done"
// Indicates a new-tab signout has been triggered and is in progress.
const NEW_TAB_SIGNOUT_PENDING_KEY = "cc_new_tab_signout_pending"
// Persists the original deep-link URL for a fresh tab through signout/login.
const NEW_TAB_TARGET_PATH_KEY = "cc_new_tab_target_path"

// Clears all per-tab auth flow state. Called on sign-out and on any auth
// failure so the next sign-in attempt in this tab starts from a clean slate
// (fresh login prompt, no stale IdP provider or return-to path).
const clearAuthSessionStorage = () => {
  sessionStorage.removeItem(FORCED_FRESH_LOGIN_KEY)
  sessionStorage.removeItem(LOGIN_RETURN_TO_KEY)
  sessionStorage.removeItem(IDP_PROVIDER_KEY)
}

const MainLayout = ({ errorMessage }: { errorMessage?: string }) => {
  const [errorMessageState, setErrorMessageState] = useState(errorMessage)
  const [userSignedInState, setUserSignedInState] = useState("checking")
  const [suppressAutoLogin, setSuppressAutoLogin] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch: any = useDispatch()
  const { buildingId, equipmentId } = useParams()
  const [page, setPage] = useState("loading")
  const [firstSignIn, setFirstSignIn] = useState(false)
  const [initializedApp, setInitializedApp] = useState(false)
  // Tracks whether the user accepted T&C in this session so the EULA check
  // effect doesn't re-show T&C while the server save is still in-flight.
  const eulaAcceptedRef = useRef(false)
  const [customProvider] = useState<string>("b2c")
  const { externalLinks } = getConfig()

  const { onSubmit: updateOwnUser } = useMutation({
    query: UPDATE_OWN_SELF,
    dataPath: "data",
    onSuccess: (data) => {
      refetch({ id: user.attributes["custom:userId"] })
    }
  })

  const { onSubmit: refreshUserSession } = useMutation({
    query: UPDATE_USER_SESSION,
    onSuccess: async (res) => {
      if (res?.["data"]?.updateUserSession) {
        const cognitoUser = await Auth.currentAuthenticatedUser()
        const currentSession = await Auth.currentSession()
        await cognitoUser.refreshSession(
          currentSession["refreshToken"],
          async (err, session) => {
            if (!err) {
              setInitializedApp(true)
              setFirstSignIn(false)
            } else {
              console.log(err)
            }
          }
        )
      } else {
        setPage("error")
        setErrorMessageState(
          "Unable to update user's access. Please try again or contact support."
        )
      }
    }
  })

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", (data: any) => {
      // v6 event name mappings from v5:
      // signIn          → signedIn
      // signIn_failure  → signInWithRedirect_failure
      // customState_failure → signInWithRedirect_failure (custom state errors surface here)
      // updateUserAttributes_failure → no direct equivalent; handled via try/catch
      const event = data?.payload?.event
      if (event === "signInWithRedirect_failure") {
        clearAuthSessionStorage()
        sessionStorage.removeItem(NEW_TAB_SIGNOUT_DONE_KEY)
        sessionStorage.removeItem(NEW_TAB_SIGNOUT_PENDING_KEY)
        sessionStorage.removeItem(NEW_TAB_TARGET_PATH_KEY)
        setSuppressAutoLogin(false)
        const msg = data?.payload?.data?.message
        if (msg) setErrorMessageState(msg)
        setPage("error")
      } else if (event === "signedIn" || event === "signIn") {
        sessionStorage.removeItem(NEW_TAB_SIGNOUT_DONE_KEY)
        sessionStorage.removeItem(NEW_TAB_SIGNOUT_PENDING_KEY)
        sessionStorage.removeItem(NEW_TAB_TARGET_PATH_KEY)
        setSuppressAutoLogin(false)
        setFirstSignIn(true)
        // OAuth callback code exchange is complete — if authFunc skipped the
        // session check (because ?code= was in the URL), mark as signed in so
        // the sign-in redirect effect doesn't incorrectly fire.
        setUserSignedInState("signed in")
      } else if (event === "signedOut") {
        if (sessionStorage.getItem(NEW_TAB_SIGNOUT_PENDING_KEY)) {
          sessionStorage.removeItem(NEW_TAB_SIGNOUT_PENDING_KEY)
          sessionStorage.setItem(NEW_TAB_SIGNOUT_DONE_KEY, "true")
        }

        // Clear Redux user state on logout to prevent stale data on next login
        dispatch(setUserInfo({ data: null }))
        dispatch(setUserAccess({ type: SUCCESS, data: new Map([]) }))
        dispatch(setUserRoles([]))
        clearAuthSessionStorage()
        setPage("loading")
        setUserSignedInState("not signed in")
      } else if (event === "customOAuthState") {
        navigate(data.payload.data)
      }
    })
    return unsubscribe
  }, [navigate, dispatch])

  // TODO: We need to handle prod environment:
  // IdP discovery is now performed dynamically using guest IAM via Cognito Identity Pool

  useEffect(() => {
    const authFunc = async () => {
      // When Cognito redirects back to the app after login, the URL contains
      // ?code=...&state=... while Amplify is still exchanging the code for tokens.
      // Calling currentSession() at this point returns "No current user" because
      // tokens haven't been stored yet, which incorrectly triggers proceedToSignIn()
      // and causes an infinite redirect loop. Skip the check and let the Hub
      // "signedIn" event drive the state transition instead.
      const params = new URLSearchParams(window.location.search)
      if (params.has('code') || params.has('error')) return

      if (
        isFedRampEnvironment() &&
        !sessionStorage.getItem(FORCED_FRESH_LOGIN_KEY)
      ) {
        if (!sessionStorage.getItem(NEW_TAB_TARGET_PATH_KEY)) {
          sessionStorage.setItem(
            NEW_TAB_TARGET_PATH_KEY,
            `${window.location.pathname}${window.location.search}`
          )
        }

        // New tab: force one signout attempt so the app starts from a clean
        // auth state before user acknowledgement on the banner.
        if (!sessionStorage.getItem(NEW_TAB_SIGNOUT_DONE_KEY)) {
          if (sessionStorage.getItem(NEW_TAB_SIGNOUT_PENDING_KEY)) {
            return
          }

          sessionStorage.setItem(NEW_TAB_SIGNOUT_PENDING_KEY, "true")
          try {
            await Auth.signOut()
          } catch (error) {
            sessionStorage.removeItem(NEW_TAB_SIGNOUT_DONE_KEY)
            sessionStorage.removeItem(NEW_TAB_SIGNOUT_PENDING_KEY)
            console.log(error)
          }

          // Do not show banner in the same cycle as signout trigger.
          // The signedOut event + loading effect will transition to banner.
          return
        }

        setUserSignedInState("not signed in")
        setPage("governmentBanner")
        return
      }

      await Auth.currentSession().catch((err) => {
        if (err === "No current user") {
          setUserSignedInState("not signed in")
        }
      })
    }
    authFunc()
  }, [])

  const proceedToSignIn = async (idpProviderName?: string | null) => {
    setSuppressAutoLogin(false)
    const returnToPath =
      sessionStorage.getItem(LOGIN_RETURN_TO_KEY) ||
      sessionStorage.getItem(NEW_TAB_TARGET_PATH_KEY) ||
      `${window.location.pathname}${window.location.search}`
    const storedProviderName = sessionStorage.getItem(IDP_PROVIDER_KEY)
    const resolvedProvider =
      idpProviderName ||
      (storedProviderName && isValidProviderName(storedProviderName)
        ? storedProviderName
        : null) ||
      customProvider
    try {
      if (isFedRampEnvironment()) {
        // Persist provider for retries in the same tab.
        if (idpProviderName) {
          sessionStorage.setItem(IDP_PROVIDER_KEY, idpProviderName)
        }
        sessionStorage.setItem(LOGIN_RETURN_TO_KEY, returnToPath)
        sessionStorage.setItem(FORCED_FRESH_LOGIN_KEY, "true")
        await Auth.federatedSignIn({
          customProvider: resolvedProvider,
          customState: returnToPath
        })
      } else {
        await Auth.federatedSignIn({
          customProvider: resolvedProvider,
          customState: `${window.location.pathname}${window.location.search}`
        })
      }
    } catch (error: any) {
      clearAuthSessionStorage()
      setPage("error")
      setErrorMessageState(
        error?.message || "Unable to start sign-in. Please try again."
      )
    }
  }

  useEffect(() => {
    if (
      userSignedInState === "not signed in" &&
      page === "loading" &&
      !suppressAutoLogin
    ) {
      if (!errorMessageState) {

        if (
          isFedRampEnvironment() &&
          !sessionStorage.getItem(FORCED_FRESH_LOGIN_KEY)
        ) {
          if (
            !sessionStorage.getItem(NEW_TAB_SIGNOUT_DONE_KEY) ||
            sessionStorage.getItem(NEW_TAB_SIGNOUT_PENDING_KEY)
          ) {
            return
          }
          setPage("governmentBanner")
        } else {
          void proceedToSignIn()
        }
      } else {
        setPage("error")
      }
    }
  }, [userSignedInState, page, suppressAutoLogin])

  const handleSignOut = async () => {
    clearAuthSessionStorage()
    setInitializedApp(false)
    setSuppressAutoLogin(!isFedRampEnvironment())
    setPage("loading")

    try {
      await Auth.signOut()
    } catch (error) {
      setSuppressAutoLogin(false)
      console.log(error)
      setPage("error")
      setErrorMessageState("Unable to log out. Please try again.")
    }
  }

  const { loading: loadingSummary } = useSelector(selectBuildingSummary)
  const { loading: loadingEquipmentSummary } = useSelector(
    selectEquipmentSummary
  )
  const { breadcrumbs, headerTitle } = useSelector((store: any) => store.home)
  const { user: _user } = useAuthenticator()
  const [userAttributes, setUserAttributes] = useState<Record<string, any>>({})
  // In Amplify v6 useAuthenticator() returns AuthUser without attributes.
  // Fetch them separately and merge so all user.attributes accesses keep working.
  useEffect(() => {
    if (_user) {
      fetchUserAttributes()
        .then((attrs) => setUserAttributes(attrs as Record<string, any>))
        .catch(() => {})
    } else {
      setUserAttributes({})
    }
  }, [_user])
  // Memoize so the object reference only changes when _user or attributes actually
  // change — prevents useEffect([user,...]) from firing on every render.
  const user = useMemo(
    () => ({ ...(_user as any), attributes: userAttributes }),
    [_user, userAttributes]
  )

  const userInfo = useSelector(selectUserInfo)
  const { data: userData, refetch } = useQuery({
    query: GET_USER_BY_ID,
    disableInitialLoad: true,
    onSuccess: (rawUserInfo) => {
      const data = parseUserData(rawUserInfo)
      if (isEmpty(data)) {
        setPage("error")
        setErrorMessageState(`No Command Center Account Found.
        We were unable to log you into Command Center because there is no Command Center account associated with the email you provided. Please contact your Trane Account Manager for further assistance.`)
      } else {
        dispatch(
          setUserInfo({
            data
          })
        )
      }
    },
    onError: (err) => {
      setPage("error")
      console.log("Error: ", err)
    },

    errorPolicy: "ignore",
    dataPath: "data.getUser"
  })

  const { data: eulaData } = useQuery({
    query: GET_EULA_VERSION,
    variables: { body: JSON.stringify({ prefix: "cc/EULA/" }) },
    dataPath: "data.getLatestFile"
  })

  const { refetch: userAccessRefetch } = useQuery({
    query: GetUIAccessToken,
    disableInitialLoad: true,
    onError: () => {
      dispatch(setUserAccess({ type: FAILURE }))
    },
    onSuccess: (userAccessInfo) => {
      const parsedData = JSON.parse(JSON.parse(userAccessInfo))
      if (!parsedData?.body) {
        setPage("error")
      } else {
        const parsedBody = JSON.parse(parsedData.body)
        const items = parsedBody?.data?.listUserAccessesByUserId?.items || []
        const UIPermMap = new Map([])
        const ListOfRoles = []

        for (let i = 0; i < items.length; i++) {
          if (ListOfRoles.indexOf(items[i].role?.name) === -1) {
            ListOfRoles.push(items[i].role?.name)
          }
          for (let j = 0; j < items[i].role.permissions.items.length; j++) {
            if (
              !UIPermMap.has(items[i].role.permissions.items[j].permission.name)
            ) {
              UIPermMap.set(
                items[i].role.permissions.items[j].permission.name.replace(
                  "[resource]",
                  items[i].resource
                ),
                true
              )
            }
          }
        }
        dispatch(setUserAccess({ type: SUCCESS, data: UIPermMap }))
        dispatch(setUserRoles(ListOfRoles))
      }
    },
    errorPolicy: "ignore",
    dataPath: "data.getUIAccessTokens"
  })

  useEffect(() => {
    const callUserRefetch = async () => {
      // Guard: only refetch once the Cognito attributes have loaded and userId
      // is available. Firing with id=undefined would return no data and
      // incorrectly trigger the Terms & Conditions page.
      const userId = user.attributes["custom:userId"]
      if (user && userId && !userInfo) {
        refetch({ id: userId })
      }
    }
    callUserRefetch()
    if (user && initializedApp) {
      dispatch(setUserAccess({ type: LOADING }))
      userAccessRefetch()
    }
  }, [user, initializedApp])

  useEffect(() => {
    if (!userInfo?.id) return

    // Prefill the user details on the Zendesk Request/feeback support Bot
    // Skipping amplitude functionality on report generation pages using puppeteer

    !isReport &&
      zendeskHelper.identify(
        userInfo.firstName ?? "",
        userInfo.lastName ?? "",
        userInfo.email ?? ""
      )
  }, [userInfo])

  const ErrorBodyComponent = () => (
    <div className={"error-body-wrapper"}>
      <Typography tagType="p">
        {errorMessageState
          ? errorMessageState
          : "We were unable to log you into Command Center because there is no Command Center account associated with the email you provided. Please contact your Trane Account Manager for further assistance."}
      </Typography>
      <br />
      <Typography tagType="p">
        <>
          <Button
            type="button"
            variant="transparent"
            onClick={() => {
              handleSignOut()
            }}
          >
            <Typography tagType="span" styleType="p3" fontType="normalFont">
              Logout
            </Typography>
          </Button>{" "}
        </>
      </Typography>
    </div>
  )

  const TermsAndConditionsBodyComponent = () => (
    <div className={"error-body-wrapper"}>
      <Typography tagType="p">
        {
          <>
            In order to continue, you must accept and agree to be bound by the
            Trane Connect{" "}
            <a
              href={`${staticFilesPath}/${JSON.parse(JSON.parse(eulaData).body).Key
                }`}
              target="_blank"
              rel="noreferrer"
            >
              End-User License Agreement Terms and Conditions
            </a>
            .
          </>
        }
      </Typography>
      <br />
      <Typography tagType="p">
        <>
          <Button
            type="button"
            variant="transparent"
            onClick={() => {
              const EULAData = JSON.parse(JSON.parse(eulaData).body)
              const profileSettings = {
                ...(userData.profileSettings
                  ? JSON.parse(userData.profileSettings)
                  : {})
              }
              profileSettings.cc = { ...(userData.profileSettings?.cc || {}) }
              profileSettings.cc.EULA = {
                file: EULAData.Key,
                date: EULAData.LastModified,
                dateSigned: new Date().valueOf()
              }

              const data = {
                ...userData,
                profileSettings: { ...profileSettings }
              }
              eulaAcceptedRef.current = true
              updateOwnUser({ requestBody: JSON.stringify(data) })
              setPage("main")
              // initializedApp gates the <Outlet /> render. Set it now so
              // tiles appear immediately — don't wait for the save round-trip.
              setInitializedApp(true)
            }}
          >
            <Typography tagType="span" styleType="p3" fontType="normalFont">
              Accept
            </Typography>
          </Button>{" "}
          <Button
            type="button"
            variant="transparent"
            onClick={() => {
              handleSignOut()
            }}
          >
            <Typography tagType="span" styleType="p3" fontType="normalFont">
              Decline
            </Typography>
          </Button>{" "}
        </>
      </Typography>
    </div>
  )

  const userName = user?.attributes?.name || ""
  const loadingData = useMemo(
    () =>
      location.pathname.includes("equipment")
        ? loadingEquipmentSummary
        : location.pathname.includes("building") &&
        location.pathname.includes("summary") &&
        loadingSummary,
    [loadingSummary, loadingEquipmentSummary]
  )
  useEffect(() => {
    if (location?.pathname === "/buildings") {
      dispatch(setBreadcrumbs([]))
      dispatch(setHeaderTitle("default"))
    }
  }, [location])

  useEffect(() => {
    dispatch(fetchSession())
  }, [])

  useEffect(() => {
    const uDataFunc = async () => {
      if (userData && eulaData) {
        try {
          if (userData.profileSettings) {
            const EULAData = JSON.parse(JSON.parse(eulaData).body)
            const profileSettings = JSON.parse(userData.profileSettings)
            if (
              profileSettings?.cc?.EULA &&
              profileSettings.cc.EULA.file === EULAData.Key &&
              profileSettings.cc.EULA.date === EULAData.LastModified
            ) {
              const userDataToSend = {
                "user id": userData?.id
                  ? userData?.id
                  : store?.getState()?.userData?.userInfo?.data?.id,
                "user type": userData?.type?.name
                  ? userData?.type?.name
                  : store?.getState()?.userData?.userInfo?.data?.type?.name,
                "role names": store?.getState()?.userData?.roles,
                "client id": "Command Center",
                "job title": userData?.jobTitle
                  ? userData?.jobTitle
                  : store?.getState()?.userData?.userInfo?.data?.jobTitle,
                "primary sales office": userData?.primarySalesOfficeId
                  ? userData?.primarySalesOfficeId
                  : store?.getState()?.userData?.userInfo?.data
                    ?.primarySalesOfficeId,
                version: import.meta.env?.VITE_AWS_JOB_ID
              }
              identifyEvent(userDataToSend)
              const profileSettings =
                store?.getState()?.userData?.userInfo?.data?.profileSettings
              const lang = JSON.parse(profileSettings)?.language
              if (lang) {
                await languageChange(lang)
              }
              if (firstSignIn) {
                await refreshUserSession({})
                const userDataToSend = {
                  "user id": userData?.id
                    ? userData?.id
                    : store?.getState()?.userData?.userInfo?.data?.id,
                  "user type": userData?.type?.name
                    ? userData?.type?.name
                    : store?.getState()?.userData?.userInfo?.data?.type?.name,
                  "role names": store?.getState()?.userData?.roles,
                  "client id": "Command Center",
                  "job title": userData?.jobTitle
                    ? userData?.jobTitle
                    : store?.getState()?.userData?.userInfo?.data?.jobTitle,
                  "primary sales office": userData?.primarySalesOfficeId
                    ? userData?.primarySalesOfficeId
                    : store?.getState()?.userData?.userInfo?.data
                      ?.primarySalesOfficeId,
                  version: import.meta.env?.VITE_AWS_JOB_ID
                }
                identifyEvent(userDataToSend)
                trackEvent(USER_EVENTS.LOGIN.events.USER_LOGIN)
              } else {
                setInitializedApp(true)
              }
            } else {
              if (!eulaAcceptedRef.current) setPage("termsAndConditions")
            }
          } else {
            if (!eulaAcceptedRef.current) setPage("termsAndConditions")
          }
        } catch (e) {
          console.log(e)
        }
      }
    }
    uDataFunc()
  }, [userData, eulaData])

  useEffect(() => {
    if (store?.getState()?.userData?.roles.length) {
      const userDataToSend = {
        "user id": store?.getState()?.userData?.userInfo?.data?.id,
        "user type": store?.getState()?.userData?.userInfo?.data?.type?.name,
        "role names": store?.getState()?.userData?.roles,
        "client id": "Command Center",
        "job title": store?.getState()?.userData?.userInfo?.data?.jobTitle,
        "primary sales office":
          store?.getState()?.userData?.userInfo?.data?.primarySalesOfficeId,
        version: import.meta.env?.VITE_AWS_JOB_ID
      }
      identifyEvent(userDataToSend)
    }
  }, [store?.getState()?.userData?.roles])

  const userAccess = useSelector(selectUserAccess)

  const languageChange = async (lang) => {
    const userData = store?.getState()?.userData
    const profileSettings = JSON.parse(
      userData?.userInfo?.data?.profileSettings
    )
    const UOM =
      profileSettings?.unitOfMeasurement === "im"
        ? "IP"
        : profileSettings?.unitOfMeasurement === "si"
          ? "SI"
          : "IP"

    // Update User selected language as preferred language in User profile
    if (profileSettings?.language !== lang) {
      const data = {
        ...userData,
        profileSettings: { ...profileSettings, language: lang }
      }
      updateOwnUser({ requestBody: JSON.stringify(data) })
    }
    localStorage.setItem("locale", lang)
    await i18n.changeLanguage(lang)
    await setMomentLocale(lang)
    dispatch(clearTranslations())
    dispatch(setSelectedLanguage(lang))
    dispatch(
      setFilter({
        language: lang,
        systemOfMeasure: UOM
      })
    )
  }

  const optionsTranslated = {
    trane: translate("Trane"),
    buildId: translate("Build ID"),
    version: translate("Version")
  }

  const menus = [
    accessControlFunc({ id: "cc.pages.adminSettings", userAccess })
      ? {
        title: <TranslateComponent>Admin Settings</TranslateComponent>,
        type: "link",
        link: "/admin-settings/permissions/groups",
        handler: () => {
          trackEvent(
            USER_EVENTS.BUILDING_SUMMARY.events.NAVIGATE_ADMIN_SETTINGS
          )
        }
      }
      : undefined,
    {
      title: <TranslateComponent>Language</TranslateComponent>,
      type: "text",
      options: SUPPORTED_LANGUAGES.map((lang) => ({
        title: lang.value,
        customTranslationOn: true,
        type: "button",
        handler: () => {
          languageChange(lang.key)
        }
      }))
    },
    {
      title: "Support / Feedback",
      type: "button",
      translateCurrentViewOff: false,
      handler: () => {
        // Skipping amplitude functionality on report generation pages using puppeteer
        if (isFedRampEnvironment()) {
          dispatch(
            setApplicationErrors({
              heading: "Need help?",
              errorMessage:
                "For assistance, please contact your local support office. They can help with questions, troubleshooting, and next steps.",
              shouldNotExpire: true
            })
          )
        } else {
          !isReport && zendeskHelper.openWidget()
        }
        trackEvent(USER_EVENTS.BUILDING_SUMMARY.events.CLICK_SUPPORT_FEEDBACK)
      }
    },
    {
      title: "License Agreement",
      type: "button",
      translateCurrentViewOff: false,
      handler: () => {
        const link = document.createElement("a")
        link.download = "EndUserLicenseAgreement.pdf"
        link.target = "_blank"
        link.href = `${staticFilesPath}/${JSON.parse(JSON.parse(eulaData).body).Key
          }`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    {
      title: "Logout",
      type: "button",
      translateCurrentViewOff: false,
      handler: () => {
        handleSignOut()
        trackEvent(USER_EVENTS.BUILDING_SUMMARY.events.CLICK_LOGOUT)
      }
    },
    { type: "divider", disabled: false },
    // accessControlFunc({ id: "tc.header.gen4link", userAccess }) ? {
    //   title: "Command Center Gen-4", type: "button", translateCurrentViewOff: false, handler: () => {
    //     window.open(externalLinks[COMMAND_CENTER_GEN_4_REDIRECTION]?.absoluteURL, "_blank")
    //     trackEvent(USER_EVENTS.BUILDING_SUMMARY.events.CLICK_COMMAND_CENTER_GEN4)
    //   }
    // } : undefined,
    {
      title: `${optionsTranslated.trane} ${new Date()?.getFullYear()} | ${optionsTranslated.buildId
        } ${import.meta.env?.VITE_AWS_JOB_ID
          ? import.meta.env?.VITE_AWS_JOB_ID.replace(/^0+/, "")
          : ""
        }`,
      type: "text",
      icon: "icon-copy-rght",
      disabled: true,
      iconLeft: true
    },
    {
      title: `${optionsTranslated.version}: ${import.meta.env?.VITE_AWS_COMMIT_ID
          ? import.meta.env?.VITE_AWS_COMMIT_ID?.substring(0, 7)
          : ""
        }`,
      type: "text",
      disabled: true
    }
  ].filter((e) => e)
  const title = useMemo(() => {
    let title = headerTitle
    if (headerTitle === "default") {
      title = <TranslateComponent>Command Center</TranslateComponent>
    }
    return title
  }, [headerTitle])

  //commented the below code as we are using ErrorPage to show respective message
  // useEffect(() => {
  //   const pathName = location?.pathname
  //   const isBuildingLevel = pathName?.includes("building") && !pathName?.includes("equipment") && !pathName?.includes("buildings")
  //   const isEquipmentLevel = pathName?.includes("building") && pathName?.includes("equipment")

  //   const equipmentIds = decodeURLSearchParams(equipmentId)
  //   const everyEquipValidUUID = equipmentIds?.every((id) => isValidUUID(id))
  //   if ((isBuildingLevel && buildingId && !isValidUUID(buildingId)) || (isEquipmentLevel && ((buildingId && !isValidUUID(buildingId)) || (equipmentId && !(everyEquipValidUUID))))) {
  //     dispatch(setApplicationErrors({ heading: itemNotFoundTitle, ErrorBody: ItemNotFound, shouldNotExpire: true }))
  //   }

  // }, [location?.pathname])

  return page === "governmentBanner" ? (
    <GovernmentBanner
      onAcknowledge={(email, idpProviderName) => {
        void proceedToSignIn(idpProviderName)
      }}
    />
  ) : page === "termsAndConditions" ? (
    <ErrorMessage
      title={"Terms & Conditions"}
      BodyComponent={TermsAndConditionsBodyComponent}
    />
  ) : page !== "error" ? (
    <>
      <Header
        userName={userName}
        options={menus}
        breadcrumbs={breadcrumbs}
        headerTitle={title}
        loading={loadingData}
      />
      {initializedApp ? <Outlet /> : <></>}
    </>
  ) : (
    <ErrorMessage
      title={errorMessageState ? "ERROR" : "No Command Center Account Found"}
      BodyComponent={ErrorBodyComponent}
    />
  )
}

export default MainLayout
